import { checkPortalSlugAvailability } from '../../api/storefront/storefront';
import {
  formatPortalSlugCooldownMessage,
  getPortalSlugChangeCooldownStatus
} from './portalSlugCooldown';

export const RESERVED_PORTAL_SEGMENTS = new Set([
  'login',
  'register',
  'dashboard',
  'reset-password'
]);

export const isReservedPortalSegment = (value) =>
  RESERVED_PORTAL_SEGMENTS.has(String(value || '').toLowerCase());

/**
 * Normalizes a company name into a URL-safe slug.
 * @param {string} name - Company name
 * @returns {string} - Slug (lowercase, alphanumeric and hyphens only)
 */
export const normalizeCompanySlug = (name) => {
  if (!name || typeof name !== 'string') return '';
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

/**
 * Checks if a portal slug is available (no other company uses it).
 * @param {string} slug - The slug to check
 * @param {string} [excludeCompanyId] - Optional company ID to exclude (e.g. when updating own slug)
 * @returns {Promise<boolean>} - true if available
 */
export const isPortalSlugAvailable = async (slug, excludeCompanyId) => {
  if (!slug || typeof slug !== 'string') return false;
  const normalized = normalizeCompanySlug(slug);
  if (!normalized) return false;

  const result = await checkPortalSlugAvailability(normalized, excludeCompanyId);
  return Boolean(result?.available);
};

export const validatePortalUrl = (url) => {
  return url
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
};

/**
 * Validates a portal slug before save: format, reserved names, and cross-company uniqueness.
 * @returns {Promise<{ ok: boolean, error: string|null, normalizedSlug: string }>}
 */
export const ensurePortalSlugCanBeSaved = async (rawSlug, companyId, options = {}) => {
  const { companyData = null, savedSlug = '' } = options;
  const normalizedSlug = normalizeCompanySlug(validatePortalUrl(rawSlug || ''));

  if (!normalizedSlug) {
    return { ok: false, error: 'Enter your website name first.', normalizedSlug: '' };
  }

  if (isReservedPortalSegment(normalizedSlug)) {
    return {
      ok: false,
      error: 'This website name is reserved. Please choose a different one.',
      normalizedSlug
    };
  }

  if (companyData) {
    const cooldown = getPortalSlugChangeCooldownStatus(companyData, normalizedSlug, savedSlug);
    if (!cooldown.allowed) {
      return {
        ok: false,
        error: formatPortalSlugCooldownMessage(cooldown),
        normalizedSlug
      };
    }
  }

  try {
    const serverResult = await checkPortalSlugAvailability(normalizedSlug, companyId);
    if (!serverResult?.available) {
      return {
        ok: false,
        error: serverResult?.reason || 'This URL is already in use. Please choose a different one.',
        normalizedSlug
      };
    }
  } catch (error) {
    console.error('Server portal slug check failed:', error);
    return {
      ok: false,
      error: 'Could not verify website name availability. Please try again.',
      normalizedSlug
    };
  }

  return { ok: true, error: null, normalizedSlug };
};

/**
 * Live field validation for the website name input (does not error on empty values).
 * @returns {Promise<{ ok: boolean, message: string|null }>}
 */
export const previewPortalSlugAvailability = async (rawSlug, companyId, savedSlug = '') => {
  const normalizedSlug = normalizeCompanySlug(validatePortalUrl(rawSlug || ''));

  if (!normalizedSlug) {
    return { ok: true, message: null };
  }

  if (normalizedSlug === normalizeCompanySlug(savedSlug)) {
    return { ok: true, message: null };
  }

  if (isReservedPortalSegment(normalizedSlug)) {
    return {
      ok: false,
      message: 'This website name is reserved. Please choose a different one.'
    };
  }

  try {
    const serverResult = await checkPortalSlugAvailability(normalizedSlug, companyId);
    if (!serverResult?.available) {
      return {
        ok: false,
        message: serverResult?.reason || 'This URL is already in use.'
      };
    }
  } catch (error) {
    console.error('Server portal slug preview failed:', error);
    return {
      ok: false,
      message: 'Could not verify website name availability. Please try again.'
    };
  }

  return { ok: true, message: null };
};
